import type { SinglePaymentChannel, Transaction } from '@/types/portal';
import { toKesEquivalent } from './invoice';

export interface LedgerSummary {
  /** Total number of ledger lines in the current view. */
  count: number;
  /** Absolute value of every line, in KES equivalent. */
  totalKes: number;
  creditsKes: number;
  debitsKes: number;
  reconciled: number;
  /** Lines still waiting on the rail — Pending. */
  pending: number;
  /** Lines not matched — Unmatched or Exception. */
  attention: number;
  unreconciled: number;
}

/** Roll the ledger up into the headline figures shown across the summary band. */
export function ledgerSummary(rows: Transaction[]): LedgerSummary {
  const creditsKes = rows
    .filter((row) => row.direction === 'Credit')
    .reduce((sum, row) => sum + toKesEquivalent(row.amount, row.currency), 0);
  const debitsKes = rows
    .filter((row) => row.direction === 'Debit')
    .reduce((sum, row) => sum + toKesEquivalent(row.amount, row.currency), 0);
  const reconciled = rows.filter((row) => row.recon === 'Reconciled').length;
  const pending = rows.filter((row) => row.recon === 'Pending').length;
  const attention = rows.filter(needsAttention).length;

  return {
    count: rows.length,
    totalKes: creditsKes + debitsKes,
    creditsKes,
    debitsKes,
    reconciled,
    pending,
    attention,
    unreconciled: rows.length - reconciled,
  };
}

export interface ChannelPosition {
  channel: SinglePaymentChannel;
  label: string;
  icon: string;
  count: number;
  amountKes: number;
}

const CHANNEL_ORDER: { channel: SinglePaymentChannel; label: string; icon: string }[] = [
  { channel: 'wallet', label: 'Wallet-to-wallet', icon: 'ri-wallet-3-line' },
  { channel: 'mobile', label: 'Mobile money', icon: 'ri-smartphone-line' },
  { channel: 'bank', label: 'Bank transfer', icon: 'ri-bank-line' },
  { channel: 'bill', label: 'Paybill', icon: 'ri-file-list-2-line' },
  { channel: 'airtime', label: 'Airtime', icon: 'ri-sim-card-line' },
  { channel: 'goods', label: 'Buy Goods', icon: 'ri-shopping-bag-3-line' },
];

/**
 * Money that has left the funding wallets, split by the channel it went out on.
 * Mirrors the channel a single payment or a whole bulk batch was sent through.
 */
export function channelPosition(rows: Transaction[]): ChannelPosition[] {
  return CHANNEL_ORDER.map(({ channel, label, icon }) => {
    const bucket = rows.filter((row) => row.channel === channel);
    return {
      channel,
      label,
      icon,
      count: bucket.length,
      amountKes: bucket.reduce((sum, row) => sum + toKesEquivalent(row.amount, row.currency), 0),
    };
  });
}

/** A ledger row needs reconciliation attention when it is not yet matched. */
export function needsAttention(row: Transaction): boolean {
  return row.recon === 'Unmatched' || row.recon === 'Exception';
}